import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

interface BackgroundMusicPlayerProps {
  active: boolean; // Chỉ phát khi ở mobile view (TikTokFeed)
  volume?: number; // Volume từ 0-1, default 0.10 (10% của audio chính)
  globalPaused?: boolean; // Đồng bộ với audio chính: true=pause, false=play
}

const BackgroundMusicPlayer: React.FC<BackgroundMusicPlayerProps> = ({ 
  active, 
  volume = 0.10,
  globalPaused = false
}) => {
  const [currentTrack, setCurrentTrack] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // ✅ REMOVED: isPausedByMainAudio - background music now follows main audio state
  const audioRef = useRef<HTMLAudioElement>(null);
  const playlistRef = useRef<string[]>([]);
  const resumeTimeoutRef = useRef<number | null>(null);
  const initTimeoutRef = useRef<number | null>(null);
  const autoPlayAttempts = useRef<number>(0);
  
  // ✅ NEW: Save playback position for out-screen resume
  const savedPlaybackPositionRef = useRef<number>(0);
  
  // ✅ NEW: Track position memory system - remember position for each track
  const trackPositionsRef = useRef<Map<string, number>>(new Map());
  const currentPostIndexRef = useRef<number>(-1);
  const usedTracksRef = useRef<Set<string>>(new Set());

  // Background music playlist - sử dụng CDN URLs
  const bgmTracks = useMemo(() => [
    'https://cdn.infonium.org/bgm/NeonGalaxy1.mp3',
    'https://cdn.infonium.org/bgm/NeonGalaxy.mp3', 
    'https://cdn.infonium.org/bgm/EthereaHorizon.mp3',
    'https://cdn.infonium.org/bgm/NeonVeins1.mp3',
    'https://cdn.infonium.org/bgm/NeonVeins.mp3',
    'https://cdn.infonium.org/bgm/VoyageIntoTomorrow.mp3',
    'https://cdn.infonium.org/bgm/backgroundmusic.mp3'
  ], []);

  // Shuffle playlist
  const shuffleArray = (array: string[]): string[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Initialize playlist immediately when component becomes active
  useEffect(() => {
    if (active && !isReady) {
      console.log('[BackgroundMusicPlayer] 🎵 Initializing playlist...');
      // Initialize immediately when active, no unnecessary delay
      initTimeoutRef.current = window.setTimeout(() => {
        playlistRef.current = shuffleArray(bgmTracks);
        if (playlistRef.current.length > 0) {
          setCurrentTrack(playlistRef.current[0]);
          setIsReady(true);
          console.log('[BackgroundMusicPlayer] ✅ Playlist ready, track set:', playlistRef.current[0]);
        }
      }, 1000); // Minimal 1 second delay to allow content to initialize
    }

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [active, isReady, bgmTracks]);

  // ✅ CRITICAL FIX: Mark audio element as background music to prevent GlobalAudioManager from stopping it
  useEffect(() => {
    if (audioRef.current) {
      // Mark this as background music so GlobalAudioManager doesn't stop it
      audioRef.current.setAttribute('data-background-music', 'true');
      audioRef.current.setAttribute('data-continuous-playback', 'true');
      console.log('[BackgroundMusicPlayer] 🎵 Marked audio as background music');
    }
  }, [currentTrack]);

  // ✅ FIXED: Enhanced autoplay with better state checking and error handling
  const attemptAutoplay = useCallback(async (reason: string) => {
    if (!audioRef.current || !isReady || !active) {
      console.log(`[BackgroundMusicPlayer] ❌ Autoplay blocked: not ready/active (${reason})`);
      return false;
    }

    if (globalPaused) {
      console.log(`[BackgroundMusicPlayer] ❌ Autoplay blocked: globally paused (${reason})`);
      return false;
    }

    if (isPlaying && !audioRef.current.paused) {
      console.log(`[BackgroundMusicPlayer] ℹ️ Already playing (${reason})`);
      return true; // Already playing
    }

    try {
      autoPlayAttempts.current++;
      console.log(`[BackgroundMusicPlayer] 🎵 Autoplay attempt #${autoPlayAttempts.current} (${reason})`);
      
      // ✅ RESTORE POSITION: Check for both out-screen position and track memory position
      let restoredPosition = 0;
      
      // Priority 1: Out-screen saved position (takes precedence)
      if (savedPlaybackPositionRef.current > 0) {
        restoredPosition = savedPlaybackPositionRef.current;
        audioRef.current.currentTime = restoredPosition;
        console.log(`[BackgroundMusicPlayer] 🔄 Restored out-screen position: ${restoredPosition.toFixed(1)}s`);
        // Clear saved position after restore
        savedPlaybackPositionRef.current = 0;
      }
      // Priority 2: Track memory position (for returning to previous tracks)
      else if (currentTrack && trackPositionsRef.current.has(currentTrack)) {
        restoredPosition = trackPositionsRef.current.get(currentTrack) || 0;
        audioRef.current.currentTime = restoredPosition;
        console.log(`[BackgroundMusicPlayer] 🔄 Restored track memory position for ${currentTrack.split('/').pop()}: ${restoredPosition.toFixed(1)}s`);
      }
      // Priority 3: Start from beginning (new track or no saved position)
      else {
        console.log(`[BackgroundMusicPlayer] ▶️ Starting track from beginning: ${currentTrack?.split('/').pop()}`);
      }
      
      await audioRef.current.play();
      
      // ✅ Ensure state is properly synced
      setIsPlaying(true);
      
      console.log(`[BackgroundMusicPlayer] ✅ Autoplay SUCCESS (${reason})`);
      return true;
    } catch (error) {
      console.log(`[BackgroundMusicPlayer] ⚠️ Autoplay failed (${reason}):`, error);
      setIsPlaying(false); // Ensure state reflects failure
      return false;
    }
  }, [isReady, active, globalPaused, isPlaying, currentTrack]);

  // Listen for user interaction to enable autoplay
  useEffect(() => {
    const handleUserInteraction = async () => {
      if (!userInteracted) {
        setUserInteracted(true);
        console.log('[BackgroundMusicPlayer] 👆 User interaction detected');
        
        // Try to start music immediately after user interaction
        const success = await attemptAutoplay('user interaction');
        if (!success && isReady && currentTrack) {
          // If still failed, keep trying periodically
          const retryInterval = setInterval(async () => {
            const retrySuccess = await attemptAutoplay('retry after interaction');
            if (retrySuccess) {
              clearInterval(retryInterval);
            }
          }, 2000);
          
          // Stop retrying after 10 seconds
          setTimeout(() => clearInterval(retryInterval), 10000);
        }
      }
    };

    // Add listeners for various user interaction types
    const events = ['touchstart', 'click', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleUserInteraction, { once: true, passive: true });
    });
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };
  }, [isReady, currentTrack, active, globalPaused, userInteracted, attemptAutoplay]);

  // ✅ ENHANCED: Smart track selection with position memory and swipe detection
  const selectTrackForPost = useCallback((postIndex: number, reason: string = 'unknown') => {
    console.log(`[BackgroundMusicPlayer] 🎯 selectTrackForPost called:`, {
      postIndex,
      reason,
      bgmTracksLength: bgmTracks.length,
      currentTrack: currentTrack?.split('/').pop()
    });
    
    if (bgmTracks.length === 0) {
      console.log('[BackgroundMusicPlayer] ❌ No BGM tracks available');
      return null;
    }
    
    // Save current track position if playing
    if (currentTrack && audioRef.current && !audioRef.current.paused) {
      const currentPosition = audioRef.current.currentTime;
      if (currentPosition > 0) {
        trackPositionsRef.current.set(currentTrack, currentPosition);
        console.log(`[BackgroundMusicPlayer] 💾 Saved position for track ${currentTrack.split('/').pop()}: ${currentPosition.toFixed(1)}s`);
      }
    }
    
    // ✅ FIXED: Ensure DIFFERENT track every time (force variety)
    let availableTracks = bgmTracks.filter(track => track !== currentTrack);
    
    console.log(`[BackgroundMusicPlayer] 🎯 Track filtering:`, {
      totalTracks: bgmTracks.length,
      currentTrack: currentTrack?.split('/').pop(),
      availableAfterFilter: availableTracks.length,
      usedTracksCount: usedTracksRef.current.size
    });
    
    // If no different tracks available (shouldn't happen with 6+ tracks), use all tracks
    if (availableTracks.length === 0) {
      console.log('[BackgroundMusicPlayer] ⚠️ No different tracks available, using all tracks');
      availableTracks = bgmTracks;
    }
    
    // ✅ ENHANCED: More randomness for better variety
    const now = Date.now();
    const randomFactor = Math.floor(Math.random() * 1000);
    const seed = (postIndex * 31 + now % 10000 + randomFactor) % availableTracks.length;
    const selectedIndex = Math.abs(seed) % availableTracks.length;
    const selectedTrack = availableTracks[selectedIndex];
    
    console.log(`[BackgroundMusicPlayer] 🎯 Track selection details:`, {
      availableTracksCount: availableTracks.length,
      seed,
      selectedIndex,
      selectedTrack: selectedTrack?.split('/').pop(),
      randomFactor,
      postIndex,
      willBeDifferent: selectedTrack !== currentTrack
    });
    
    // ✅ VERIFICATION: Ensure we actually selected a different track
    if (selectedTrack === currentTrack && availableTracks.length > 1) {
      console.log('[BackgroundMusicPlayer] ⚠️ Same track selected, forcing different track');
      // Find first different track
      const differentTrack = availableTracks.find(track => track !== currentTrack);
      if (differentTrack) {
        console.log(`[BackgroundMusicPlayer] 🔄 Forced different track: ${differentTrack.split('/').pop()}`);
        return differentTrack;
      }
    }
    
    // ✅ SIMPLIFIED: Remove complex used tracks management for now
    // The main filter (track !== currentTrack) already ensures variety
    
    console.log(`[BackgroundMusicPlayer] 🎵 FINAL SELECTED TRACK for post ${postIndex} (${reason}): ${selectedTrack.split('/').pop()}`);
    return selectedTrack;
  }, [bgmTracks, currentTrack]);

  // ✅ NEW: Listen for post changes (swipe events) to change background track
  useEffect(() => {
    const handlePostChange = (event: CustomEvent) => {
      const { currentIndex, previousIndex, reason } = event.detail;
      
      console.log(`[BackgroundMusicPlayer] 📱 POST CHANGE EVENT RECEIVED: ${previousIndex} → ${currentIndex} (${reason})`);
      console.log(`[BackgroundMusicPlayer] 🔍 Current state:`, {
        currentPostIndexRef: currentPostIndexRef.current,
        currentTrack: currentTrack?.split('/').pop(),
        active,
        globalPaused,
        userInteracted
      });
      
      // ✅ ENHANCED: Always try to change track on post change (ignore post index for now)
      console.log(`[BackgroundMusicPlayer] 🔄 FORCING TRACK CHANGE for every post swipe`);
      
      // Update post index
      currentPostIndexRef.current = currentIndex;
      
      // Always select new track (force variety)
      const newTrack = selectTrackForPost(currentIndex, reason);
      console.log(`[BackgroundMusicPlayer] 🎵 Selected track:`, {
        newTrack: newTrack?.split('/').pop(),
        currentTrack: currentTrack?.split('/').pop(),
        willChange: newTrack && newTrack !== currentTrack
      });
      
      if (newTrack && newTrack !== currentTrack) {
        console.log(`[BackgroundMusicPlayer] 🔄 EXECUTING TRACK CHANGE: ${currentTrack?.split('/').pop()} → ${newTrack.split('/').pop()}`);
        
        // ✅ SIMPLIFIED: Just set the new track, useEffect will handle auto-play
        setCurrentTrack(newTrack);
      } else if (newTrack === currentTrack) {
        console.log(`[BackgroundMusicPlayer] ⚠️ SAME TRACK SELECTED - This should not happen with new logic!`);
        
        // ✅ FALLBACK: Force select a different track
        const allOtherTracks = bgmTracks.filter(track => track !== currentTrack);
        if (allOtherTracks.length > 0) {
          const fallbackTrack = allOtherTracks[Math.floor(Math.random() * allOtherTracks.length)];
          console.log(`[BackgroundMusicPlayer] 🆘 FALLBACK: Using random different track: ${fallbackTrack.split('/').pop()}`);
          setCurrentTrack(fallbackTrack);
        }
      } else {
        console.log(`[BackgroundMusicPlayer] ❌ NO TRACK SELECTED - This is an error!`);
      }
    };

    // Listen for post change events
    window.addEventListener('postChange', handlePostChange as EventListener);
    
    return () => {
      window.removeEventListener('postChange', handlePostChange as EventListener);
    };
  }, [currentTrack, selectTrackForPost, bgmTracks, active, globalPaused, userInteracted]);

  // ✅ NEW: Initialize with current post on first load
  useEffect(() => {
    console.log('[BackgroundMusicPlayer] 🎬 Initialization check:', {
      hasCurrentTrack: !!currentTrack,
      active,
      bgmTracksLength: bgmTracks.length,
      shouldInitialize: !currentTrack && active && bgmTracks.length > 0
    });
    
    // Only initialize if we don't have a current track and we're active
    if (!currentTrack && active && bgmTracks.length > 0) {
      // Try to get current post index from TikTokFeed
      const initialIndex = 0; // Default to first post
      console.log('[BackgroundMusicPlayer] 🎬 INITIALIZING with post index:', initialIndex);
      
      const initialTrack = selectTrackForPost(initialIndex, 'initialization');
      console.log('[BackgroundMusicPlayer] 🎬 Initial track selected:', initialTrack?.split('/').pop());
      
      if (initialTrack) {
        setCurrentTrack(initialTrack);
        currentPostIndexRef.current = initialIndex;
        console.log('[BackgroundMusicPlayer] ✅ INITIALIZATION COMPLETE');
      } else {
        console.log('[BackgroundMusicPlayer] ❌ INITIALIZATION FAILED - No track selected');
      }
    }
  }, [active, currentTrack, bgmTracks.length, selectTrackForPost]);

  // ✅ NEW: Memory management - cleanup old positions to prevent memory leak
  useEffect(() => {
    const maxMemoryEntries = 20; // Keep position memory for last 20 tracks
    
    if (trackPositionsRef.current.size > maxMemoryEntries) {
      // Convert to array, keep most recent entries
      const entries = Array.from(trackPositionsRef.current.entries());
      const recentEntries = entries.slice(-maxMemoryEntries);
      
      trackPositionsRef.current.clear();
      recentEntries.forEach(([track, position]) => {
        trackPositionsRef.current.set(track, position);
      });
      
      console.log(`[BackgroundMusicPlayer] 🧹 Memory cleanup: kept ${maxMemoryEntries} most recent track positions`);
    }
  }, [currentTrack]); // Check on every track change

  // Legacy: Play next track in shuffle (for track ended)
  const playNextTrack = useCallback(() => {
    if (playlistRef.current.length === 0) return;
    
    const currentIndex = playlistRef.current.findIndex(track => track === currentTrack);
    let nextIndex = currentIndex + 1;
    
    // If reached end, reshuffle and start from beginning
    if (nextIndex >= playlistRef.current.length) {
      playlistRef.current = shuffleArray(bgmTracks);
      nextIndex = 0;
    }
    
    setCurrentTrack(playlistRef.current[nextIndex]);
  }, [currentTrack, bgmTracks]);

  // ✅ CORRECTED: Background music follows main audio state (plays WITH audio, not instead of)
  useEffect(() => {
    if (!audioRef.current || !currentTrack || !isReady) return;

    const audio = audioRef.current;
    audio.volume = volume;

    // Clear any pending timeout
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }

    // Component inactive - stop completely
    if (!active) {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
        console.log('[BackgroundMusicPlayer] 🛑 Stopped due to inactive');
      }
      return;
    }

    // 🎵 CORRECTED LOGIC: Background music should FOLLOW main audio state
    if (globalPaused) {
      // Main audio is paused (user pause or out-screen) → pause background music too
      if (isPlaying) {
        // ✅ SAVE POSITION: Save current playback position before pause (for out-screen resume)
        if (audio.currentTime > 0) {
          savedPlaybackPositionRef.current = audio.currentTime;
          console.log('[BackgroundMusicPlayer] 💾 Saved playback position:', savedPlaybackPositionRef.current);
        }
        
        audio.pause();
        setIsPlaying(false);
        console.log('[BackgroundMusicPlayer] ⏸️ Paused - following main audio pause state');
      }
    } else {
      // Main audio is playing → background music should play too (with slight delay for smooth start)
      if (!isPlaying) {
        // Add small delay to sync with main audio startup
        resumeTimeoutRef.current = window.setTimeout(async () => {
          if (audioRef.current && !globalPaused && active && isReady) {
            const success = await attemptAutoplay('syncing with main audio play');
            if (success) {
              console.log('[BackgroundMusicPlayer] ▶️ Playing - following main audio play state');
            }
          }
        }, 300); // Short delay to sync with main audio
      }
    }

    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
    };
  }, [active, currentTrack, volume, isPlaying, userInteracted, globalPaused, isReady, attemptAutoplay]);

  // ✅ DISABLED: Auto-recovery was causing sync issues - main logic handles everything now
  useEffect(() => {
    // Auto-recovery logic disabled to prevent sync conflicts
    // The main control logic above handles all pause/resume scenarios reliably
    
    // Keep minimal global cleanup listener for safety
    const handleGlobalCleanup = () => {
      console.log('[BackgroundMusicPlayer] 🧹 Global cleanup detected - will be handled by main logic');
    };

    window.addEventListener('forceAudioCleanup', handleGlobalCleanup);
    
    return () => {
      window.removeEventListener('forceAudioCleanup', handleGlobalCleanup);
    };
  }, []);

  // ✅ ENHANCED: Handle track ended with position cleanup
  const handleTrackEnded = () => {
    console.log('[BackgroundMusicPlayer] 🔄 Track ended, clearing memory and playing next');
    
    // Clear this track's saved position since it completed
    if (currentTrack) {
      trackPositionsRef.current.delete(currentTrack);
      console.log('[BackgroundMusicPlayer] 🗑️ Cleared completed track position:', currentTrack.split('/').pop());
    }
    
    setIsPlaying(false);
    playNextTrack();
  };

  // ✅ NEW: Auto-save position every 10 seconds during playback
  useEffect(() => {
    if (!currentTrack || !audioRef.current || !isPlaying) return;

    const autoSaveInterval = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused && audioRef.current.currentTime > 0) {
        const currentPosition = audioRef.current.currentTime;
        trackPositionsRef.current.set(currentTrack, currentPosition);
        console.log(`[BackgroundMusicPlayer] 💾 Auto-saved position for ${currentTrack.split('/').pop()}: ${currentPosition.toFixed(1)}s`);
      }
    }, 10000); // Save every 10 seconds

    return () => clearInterval(autoSaveInterval);
  }, [currentTrack, isPlaying]);

  // ✅ CRITICAL DEBUG: Track change detection with enhanced logging
  const prevTrackRef = useRef<string | null>(null);
  
  useEffect(() => {
    console.log(`[BackgroundMusicPlayer] 🔍 DEBUG: useEffect triggered`, {
      currentTrack: currentTrack?.split('/').pop(),
      prevTrack: prevTrackRef.current?.split('/').pop(),
      isPlaying,
      globalPaused,
      active,
      userInteracted,
      hasAudioRef: !!audioRef.current
    });
    
    // Detect track changes
    if (currentTrack && currentTrack !== prevTrackRef.current) {
      console.log(`[BackgroundMusicPlayer] 🎵 TRACK CHANGED DETECTED: ${prevTrackRef.current?.split('/').pop()} → ${currentTrack.split('/').pop()}`);
      
      // ✅ SIMPLIFIED: Force immediate load and play regardless of current state
      if (audioRef.current && active) {
        console.log(`[BackgroundMusicPlayer] ⚡ FORCE LOADING NEW TRACK`);
        
        // Force load the new track immediately
        audioRef.current.load();
        
        // If background music should be playing, trigger play immediately
        if (!globalPaused && userInteracted) {
          console.log(`[BackgroundMusicPlayer] ▶️ ATTEMPTING IMMEDIATE PLAY`);
          
          // Simple timeout approach - more reliable than event listeners
          setTimeout(async () => {
            if (audioRef.current && currentTrack) {
              try {
                console.log(`[BackgroundMusicPlayer] 🎯 EXECUTING PLAY for ${currentTrack.split('/').pop()}`);
                
                // Restore position if available
                if (trackPositionsRef.current.has(currentTrack)) {
                  const savedPosition = trackPositionsRef.current.get(currentTrack) || 0;
                  audioRef.current.currentTime = savedPosition;
                  console.log(`[BackgroundMusicPlayer] 🔄 Restored position: ${savedPosition.toFixed(1)}s`);
                }
                
                await audioRef.current.play();
                setIsPlaying(true);
                console.log(`[BackgroundMusicPlayer] ✅ NEW TRACK PLAYING SUCCESSFULLY`);
              } catch (error) {
                console.error('[BackgroundMusicPlayer] ❌ Play failed:', error);
                setIsPlaying(false);
              }
            }
          }, 100); // Short delay to ensure load has started
        }
      }
      
      prevTrackRef.current = currentTrack;
    }
  }, [currentTrack, active, globalPaused, userInteracted, isPlaying]); // ✅ FIXED: Include all dependencies

  // Update volume when prop changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, []);

  if (!currentTrack) return null;

  return (
    <audio
      ref={audioRef}
      src={currentTrack}
      loop={false} // Không loop, sẽ chuyển bài khi hết
      onEnded={handleTrackEnded}
      onLoadStart={() => {
        console.log(`[BackgroundMusicPlayer] 🔄 Loading track:`, currentTrack?.split('/').pop());
      }}
      onCanPlay={() => {
        console.log(`[BackgroundMusicPlayer] ✅ Track ready to play:`, currentTrack?.split('/').pop());
      }}
      onError={(e) => {
        console.error('[BackgroundMusicPlayer] ❌ Track load error:', e);
      }}
      style={{ display: 'none' }} // Ẩn hoàn toàn
              preload={active ? "auto" : "metadata"} // ✅ OPTIMIZED: Full preload when active for instant switching
      data-background-music="true" // Mark as background music
      data-continuous-playback="true" // Mark for continuous playback
    />
  );
};

export default BackgroundMusicPlayer; 